import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiUser, FiMessageSquare } from 'react-icons/fi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const services = [
    'Aviation Asset Management',
    'Airworthiness Management', 
    'Project Management',
    'MRO Services',
    'Global Aircraft Relocation',
    'General Inquiry'
  ];

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      info: '+64 21-221-3989',
    },
    {
      icon: FiMail,
      title: 'Email',
      info: 'hello@blacksand.aero',
    },
    {
      icon: FiMapPin,
      title: 'Location',
      info: 'New Zealand Based',
    },
    {
      icon: FiClock,
      title: 'Business Hours',
      info: '8-6 Operations',
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-midnight-navy to-midnight-navy/90 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get In <span className="text-aero-blue">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Ready to elevate your aviation operations? Contact our team of experts 
              for personalized solutions and professional consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gradient-to-br from-cloud-beige to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
            >
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-midnight-navy mb-4">
                  Send Us A Message
                </h2>
                <p className="text-shadow-gray">
                  Fill out the form below and we'll get back to you within 2 hours.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-midnight-navy mb-2">
                      <FiUser className="inline mr-2" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aero-blue focus:border-transparent transition-all duration-200"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-midnight-navy mb-2">
                      <FiMail className="inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aero-blue focus:border-transparent transition-all duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-midnight-navy mb-2">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aero-blue focus:border-transparent transition-all duration-200"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-midnight-navy mb-2">
                      Service Interest
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aero-blue focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-midnight-navy mb-2">
                    <FiMessageSquare className="inline mr-2" />
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aero-blue focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell us about your project or requirements..."
                  />
                </div>

                <motion.button
                  type="submit"
                  className="w-full bg-aero-blue text-midnight-navy py-4 rounded-lg font-semibold text-lg hover:bg-aero-blue/90 transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiSend />
                  <span>Send Message</span>
                </motion.button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-midnight-navy mb-6">
                  Contact Information
                </h2>
                <p className="text-shadow-gray text-lg leading-relaxed">
                  Get in touch with our aviation experts. We're here to help you with 
                  all your helicopter maintenance and aviation service needs.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-aero-blue/10 p-3 rounded-lg">
                        <item.icon className="text-aero-blue text-xl" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-midnight-navy mb-1">
                          {item.title}
                        </h3>
                        <p className="text-midnight-navy font-medium mb-1">
                          {item.info}
                        </p>
                        <p className="text-shadow-gray text-sm">
                          {item.subtext}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Service Areas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-midnight-navy rounded-2xl p-8 text-white"
              >
                <h3 className="text-xl font-semibold mb-4 text-aero-blue">
                  Service Coverage
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-aero-blue rounded-full"></div>
                    <span>New Zealand (Primary Base)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-aero-blue rounded-full"></div>
                    <span>North America</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-aero-blue rounded-full"></div>
                    <span>Pacific Region</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-aero-blue rounded-full"></div>
                    <span>Global Operations</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-midnight-navy mb-4">
              Our Global Presence
            </h2>
            <p className="text-shadow-gray text-lg max-w-2xl mx-auto">
              While based in New Zealand, we provide services across multiple regions 
              with our network of partners and certified technicians.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-br from-midnight-navy to-midnight-navy/90 rounded-3xl p-8 text-white text-center"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-aero-blue mb-2">Asia-Pacific</h3>
                <p className="text-gray-300">Primary operations hub in New Zealand</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-aero-blue mb-2">North America</h3>
                <p className="text-gray-300">Extended service coverage</p>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-aero-blue mb-2">Global</h3>
                <p className="text-gray-300">Project-based services worldwide</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;